/** Window-level hotkeys.
 *
 * Bindings are declared as data so the same list renders the help overlay and
 * drives the listener; a keyboard shortcut nobody can discover is a bug.
 */

import { useEffect, useMemo, useRef } from "react";
import { isTypingTarget, matchesCombo, parseCombo, type ParsedCombo } from "./keys.js";

export interface Hotkey {
  readonly combo: string;
  /** Shown in the help overlay. Required: an undocumented binding is a bug. */
  readonly description: string;
  readonly onFire: () => void;
  /** Fire while the key is held. Off by default; on for map scrolling. */
  readonly repeats?: boolean | undefined;
  readonly disabled?: boolean | undefined;
}

export function useHotkeys(hotkeys: readonly Hotkey[], enabled = true): void {
  // The handler must see the latest closures without re-binding the listener on
  // every render, so the list goes through a ref and only the parse is memoised.
  const latest = useRef(hotkeys);
  latest.current = hotkeys;

  // Re-parse only when the combo *strings* change, not when a handler identity
  // does; a fresh arrow per render is the normal case at every call site.
  const comboKey = hotkeys.map((h) => h.combo).join("|");
  const parsed = useMemo<readonly ParsedCombo[]>(
    () => (comboKey === "" ? [] : comboKey.split("|").map(parseCombo)),
    [comboKey],
  );
  const parsedRef = useRef(parsed);
  parsedRef.current = parsed;

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target;
      const el = target instanceof HTMLElement ? target : null;
      if (isTypingTarget(el?.tagName ?? null, el?.isContentEditable ?? false)) return;

      const list = latest.current;
      const combos = parsedRef.current;
      for (let i = 0; i < list.length; i++) {
        const hk = list[i];
        const combo = combos[i];
        if (hk === undefined || combo === undefined) continue;
        if (hk.disabled === true) continue;
        if (event.repeat && hk.repeats !== true) continue;
        if (!matchesCombo(event, combo)) continue;
        // preventDefault, never stopPropagation: D8 gives pointer *and* key
        // ownership to whoever the event is over, and a panel below us may want
        // the same key. Scrolling the page on space is the only thing suppressed.
        event.preventDefault();
        hk.onFire();
        return;
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled]);
}
